import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Modal } from "flowbite-react";
import AnimatedToast from "../components/common/AnimatedToast";
import html2pdf from "html2pdf.js";
import { FiTrash } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import axiosInstance from "../config/axiosInstance";
import { BUDGETS, CATEGORIES, USERS, LEADS } from "../config/apiConfig";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";

const DEFAULT_PRODUCT_CATALOG = [
  {
    id: "crm-suite",
    name: "Plataforma de CRM Completa",
    type: "Produto",
    description:
      "Suíte para organizar leads, oportunidades e automações de vendas.",
    unitPrice: 5980,
  },
  {
    id: "automation-lab",
    name: "Pacote Automação Premium",
    type: "Serviço",
    description:
      "Modelagem de automações para cadência de follow-up e alertas.",
    unitPrice: 2480,
  },
  {
    id: "implementation",
    name: "Implementação e Treinamento",
    type: "Serviço",
    description: "Onboarding guiado com treinamentos personalizados.",
    unitPrice: 4500,
  },
  {
    id: "integration",
    name: "Integrações Avançadas",
    type: "Serviço",
    description: "Conexões com ERP, WhatsApp Business e gateways de pagamento.",
    unitPrice: 3200,
  },
  {
    id: "support",
    name: "Suporte Platinum",
    type: "Serviço",
    description:
      "Suporte prioritário, consultoria mensal e alinhamento de OKRs.",
    unitPrice: 1280,
  },
];

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);

const DEFAULT_TEMPLATE_OPTIONS = [
  {
    id: "clean",
    label: "Tradicional",
    description:
      "Layout tradicional com header, detalhes e tabela em sequência.",
    layout: "default",
  },
  {
    id: "modern",
    label: "Com destaque financeiro",
    description: "Resumo financeiro destacado antes da tabela de itens.",
    layout: "summaryFirst",
  },
  {
    id: "soft",
    label: "Compacto",
    description:
      "Lista de itens centralizada com detalhes e totais lado a lado.",
    layout: "sideBySide",
  },
];

const normalizeCatalogEntries = (list = []) =>
  list.map((entry) => ({
    ...entry,
    unitPrice: Number(entry.unitPrice ?? entry.price ?? 0),
  }));

const buildReference = () => `ORC-${String(Date.now()).slice(-6)}`;

const buildDefaultValidityDate = () => {
  const next = new Date();
  next.setDate(next.getDate() + 30);
  return next.toISOString().slice(0, 10);
};

const toIsoDate = (value) => {
  if (!value) return buildDefaultValidityDate();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return buildDefaultValidityDate();
  }
  return parsed.toISOString().split("T")[0];
};

const formatIssueDate = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : moment().format("DD/MM/YYYY");

const getWindowUserId = () => {
  if (typeof window === "undefined") {
    return "";
  }
  return window?.user?.id ? String(window.user.id) : "";
};

const BudgetGeneratorPage = () => {
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState(() =>
    normalizeCatalogEntries(DEFAULT_PRODUCT_CATALOG)
  );
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [layoutTemplates, setLayoutTemplates] = useState(
    DEFAULT_TEMPLATE_OPTIONS
  );
  const [selectedProductId, setSelectedProductId] = useState(
    DEFAULT_PRODUCT_CATALOG[0]?.id || ""
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    DEFAULT_TEMPLATE_OPTIONS[0]?.id || ""
  );
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetDescription, setPresetDescription] = useState("");
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [clientAddModalOpen, setClientAddModalOpen] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientNote, setClientNote] = useState("");
  const [sellerOptions, setSellerOptions] = useState([]);
  const [selectedSellerId, setSelectedSellerId] = useState(() =>
    getWindowUserId()
  );
  const [isLoadingSellers, setIsLoadingSellers] = useState(true);
  const [leadCustomFieldSchema, setLeadCustomFieldSchema] = useState([]);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [animatedToast, setAnimatedToast] = useState(null);
  const clientSearchTimer = useRef(null);
  const pdfRef = useRef(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [validityDate, setValidityDate] = useState(() =>
    buildDefaultValidityDate()
  );
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const budgetId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("budgetId");
  }, [location.search]);
  const [referenceNumber, setReferenceNumber] = useState(() =>
    buildReference()
  );
  const [issueDate, setIssueDate] = useState(() => formatIssueDate());
  const filteredCatalog = useMemo(() => {
    let entries = catalog;
    if (selectedCategoryId) {
      entries = entries.filter(
        (product) => String(product.categoryId ?? "") === selectedCategoryId
      );
    }
    if (catalogSearch.trim()) {
      const searchTerm = catalogSearch.trim().toLowerCase();
      entries = entries.filter((product) =>
        (product.name || "").toLowerCase().includes(searchTerm)
      );
    }
    return entries;
  }, [catalog, selectedCategoryId, catalogSearch]);

  const selectedSeller = useMemo(
    () =>
      sellerOptions.find(
        (seller) => String(seller.id) === String(selectedSellerId)
      ) || null,
    [sellerOptions, selectedSellerId]
  );

  useEffect(() => {
    if (!filteredCatalog.length) {
      setSelectedProductId("");
      return;
    }
    setSelectedProductId((prev) =>
      filteredCatalog.some((product) => product.id === prev)
        ? prev
        : filteredCatalog[0].id
    );
  }, [filteredCatalog]);

  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const response = await axiosInstance.get(CATEGORIES.GET_ALL);
      setCategories(response.data?.data ?? []);
    } catch (error) {
      console.error(
        "[BudgetGeneratorPage] falha ao carregar categorias",
        error
      );
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!isCatalogModalOpen) {
      setSelectedCatalogIds([]);
    }
  }, [isCatalogModalOpen]);

  useEffect(() => {
    let active = true;
    const loadCustomFields = async () => {
      try {
        const response = await axiosInstance.get(LEADS.CUSTOM_FIELDS);
        const payload = response?.data?.data;
        if (active) {
          setLeadCustomFieldSchema(
            Array.isArray(payload) ? payload : Array.isArray(response?.data)
              ? response.data
              : []
          );
        }
      } catch (error) {
        console.error(
          "[BudgetGeneratorPage] falha ao carregar campos customizados",
          error
        );
      }
    };
    loadCustomFields();
    return () => {
      active = false;
    };
  }, []);

  const fetchSellers = useCallback(async () => {
    setIsLoadingSellers(true);
    try {
      const response = await axiosInstance.get(
        `${USERS.GET_USERS}?itemsPerPage=-1`
      );
      const payload =
        response?.data?.data?.data ||
        response?.data?.data ||
        response?.data ||
        [];
      const sellers = Array.isArray(payload) ? payload : [];
      setSellerOptions(sellers);
      setSelectedSellerId((prev) => {
        if (
          prev &&
          sellers.some((seller) => String(seller.id) === String(prev))
        ) {
          return prev;
        }
        const windowId = getWindowUserId();
        if (
          windowId &&
          sellers.some((seller) => String(seller.id) === String(windowId))
        ) {
          return windowId;
        }
        return sellers[0]?.id ? String(sellers[0].id) : "";
      });
    } catch (error) {
      console.error(
        "[BudgetGeneratorPage] falha ao carregar vendedores",
        error
      );
    } finally {
      setIsLoadingSellers(false);
    }
  }, []);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axiosInstance.get(BUDGETS.TEMPLATES);
        const payload = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        if (payload.length) {
          const enriched = payload.map((item) => ({
            ...item,
            layout: item.layout || "default",
          }));
          setLayoutTemplates(enriched);
          setSelectedTemplateId((prev) =>
            enriched.some((option) => option.id === prev)
              ? prev
              : enriched[0].id
          );
        }
      } catch (error) {
        console.error(
          "[BudgetGeneratorPage] não foi possível carregar os templates",
          error
        );
      }
    };

    const fetchCatalog = async () => {
      try {
        const response = await axiosInstance.get(BUDGETS.CATALOG);
        const payload = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        if (payload.length) {
          const normalized = normalizeCatalogEntries(payload);
          setCatalog(normalized);
          setSelectedProductId((prev) =>
            normalized.some((item) => item.id === prev)
              ? prev
              : normalized[0].id
          );
        }
      } catch (error) {
        console.error(
          "[BudgetGeneratorPage] não foi possível carregar o catálogo",
          error
        );
      }
    };

    fetchTemplates();
    fetchCatalog();
  }, []);

  const loadSavedTemplates = useCallback(async () => {
    try {
      const response = await axiosInstance.get(BUDGETS.PRESETS);
      const payload = Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setSavedTemplates(payload);
      setSelectedPresetId((prev) =>
        payload.some((preset) => preset.id === prev)
          ? prev
          : payload[0]?.id || ""
      );
    } catch (error) {
      console.error(
        "[BudgetGeneratorPage] não foi possível carregar templates salvos",
        error
      );
    }
  }, []);

  useEffect(() => {
    loadSavedTemplates();
  }, [loadSavedTemplates]);

  useEffect(() => {
    if (!budgetId) {
      return;
    }

    const fetchBudget = async () => {
      setNotification(null);
      setItems([]);
      try {
        const response = await axiosInstance.get(BUDGETS.GET(budgetId));
        const fetched = response.data?.data;
        if (!fetched) {
          return;
        }

        setReferenceNumber(fetched.reference || buildReference());
        setIssueDate(formatIssueDate(fetched.issuedAt));
        setValidityDate(toIsoDate(fetched.validUntil));
        setClientName(fetched.clientName || "");
        setClientEmail(fetched.clientEmail || "");
        setClientNote(fetched.clientNote || "");
        if (fetched.templateId) {
          setSelectedTemplateId(fetched.templateId);
        }
        const sellerId =
          fetched.ownerId ??
          fetched.sellerId ??
          fetched.owner?.id ??
          fetched.createdById ??
          fetched.creator?.id;
        setSelectedSellerId(
          sellerId !== undefined && sellerId !== null
            ? String(sellerId)
            : getWindowUserId()
        );

        const mappedItems = Array.isArray(fetched.items)
          ? fetched.items.map((item) => ({
              id: item.id,
              productId: item.productId,
              name: item.name,
              description: item.description,
              type: item.type,
              quantity: Number(item.quantity) || 1,
              unitPrice: Number(item.unitPrice) || 0,
              discount: Number(item.discountPercentage) || 0,
              unit: item.unit || null,
            }))
          : [];
        setItems(mappedItems);
      } catch (error) {
        console.error(
          "[BudgetGeneratorPage] falha ao carregar o orçamento",
          error
        );
        setNotification({
          type: "danger",
          message:
            error?.response?.data?.message ||
            "Não foi possível carregar o orçamento solicitado.",
        });
      }
    };

    fetchBudget();
  }, [budgetId]);

  useEffect(() => {
    return () => {
      if (clientSearchTimer.current) {
        clearTimeout(clientSearchTimer.current);
      }
    };
  }, []);

  const addProductToBudget = (product, { closeModal = true } = {}) => {
    if (!product) return;
    setItems((prev) => [
      ...prev,
      {
        id: `${product.id}-${Date.now()}`,
        productId: product.id,
        name: product.name,
        description: product.description,
        type: product.type,
        quantity: 1,
        unitPrice: Number(product.unitPrice ?? 0),
        discount: 0,
        unit: product.unit || null,
      },
    ]);
    if (closeModal) {
      setIsCatalogModalOpen(false);
    }
  };

  const toggleCatalogSelection = (productId) => {
    const id = String(productId);
    setSelectedCatalogIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((value) => value !== id);
      }
      return [...prev, id];
    });
  };

  const handleCatalogItemClick = (product) => {
    setSelectedProductId(product.id);
    toggleCatalogSelection(product.id);
  };

  const handleAddSelectedCatalogItems = () => {
    if (!selectedCatalogIds.length) return;
    const productsToAdd = filteredCatalog.filter((product) =>
      selectedCatalogIds.includes(String(product.id))
    );
    productsToAdd.forEach((product) =>
      addProductToBudget(product, { closeModal: false })
    );
    setSelectedCatalogIds([]);
  };

  const formatCustomFieldValueForPayload = (field, value) => {
    let normalized = value;
    const type = String(field?.type || "").toLowerCase();
    if (["checkbox", "boolean"].includes(type)) {
      normalized =
        normalized === true ||
        normalized === "true" ||
        normalized === "1" ||
        normalized === 1
          ? "true"
          : "false";
    }
    if (normalized === undefined || normalized === null) {
      normalized = "";
    }
    return normalized;
  };

  const guessCustomFieldValue = (field) => {
    if (!field || !field.id) {
      return "";
    }
    const normalizedType = String(field.type || "").toLowerCase();
    const baseValue = field.defaultValue ?? "";
    if (!field.isRequired) {
      return baseValue;
    }
    if (["checkbox", "boolean"].includes(normalizedType)) {
      return true;
    }
    if (["number", "currency", "percent"].includes(normalizedType)) {
      return newContactPhone.trim();
    }
    return newContactName.trim() || baseValue;
  };

  const updateItem = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]:
                field === "quantity"
                  ? Math.max(1, Number(value) || 1)
                  : Math.min(100, Math.max(0, Number(value) || 0)),
            }
          : item
      )
    );
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((item) => item.id !== id));

  const handleApplyPreset = () => {
    if (!selectedPresetId) return;
    const preset = savedTemplates.find((tpl) => tpl.id === selectedPresetId);
    if (!preset) return;
    if (
      items.length &&
      !window.confirm(
        "Aplicar este template irá substituir os itens atuais. Deseja continuar?"
      )
    ) {
      return;
    }
    const newItems = (preset.items ?? []).map((item, index) => ({
      id: `${preset.id}-${Date.now()}-${index}`,
      productId: item.productId,
      name: item.name,
      description: item.description,
      type: item.type,
      quantity: Number(item.quantity) || 1,
      unitPrice: Number(item.unitPrice) || 0,
      discount: Number(item.discountPercentage ?? (item.discount || 0)),
      unit: item.unit || null,
    }));
    setItems(newItems);
  };

  const handleSavePreset = async () => {
    if (!items.length) {
      setNotification({
        type: "danger",
        message: "Adicione itens antes de salvar um template.",
      });
      return;
    }
    const name = presetName.trim();
    if (!name) {
      setNotification({
        type: "danger",
        message: "Informe um nome para o template.",
      });
      return;
    }
    setIsSavingPreset(true);
    try {
      const payloadItems = items.map((item) => ({
        productId: item.productId || null,
        name: item.name,
        description: item.description || null,
        type: item.type || null,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        discountPercentage: Math.min(
          100,
          Math.max(Number(item.discount) || 0, 0)
        ),
        unit: item.unit || null,
      }));
      const payload = {
        name,
        description: presetDescription.trim() || null,
        items: payloadItems,
      };
      const response = await axiosInstance.post(BUDGETS.PRESETS, payload);
      setNotification({
        type: "success",
        message: "Template salvo com sucesso.",
      });
      setAnimatedToast({
        id: Date.now(),
        message: "Template salvo com sucesso.",
      });
      setPresetModalOpen(false);
      setPresetName("");
      setPresetDescription("");
      await loadSavedTemplates();
      setSelectedPresetId(response.data?.data?.id || "");
    } catch (error) {
      const message =
        error?.response?.data?.message || "Não foi possível salvar o template.";
      setNotification({ type: "danger", message });
    } finally {
      setIsSavingPreset(false);
    }
  };

  const fetchClientSuggestions = useCallback(async (query) => {
    if (!query) {
      setClientSuggestions([]);
      return;
    }
    try {
      const response = await axiosInstance.get(BUDGETS.CLIENTS, {
        params: { q: query },
      });
      const payload = response.data?.data ?? [];
      setClientSuggestions(payload);
      setShowClientSuggestions(true);
    } catch (error) {
      console.error("fetchClientSuggestions:", error);
    }
  }, []);

  const handleClientNameChange = (value) => {
    setClientName(value);
    const trimmed = value?.trim() || "";
    const normalized = trimmed.toLowerCase();
    if (clientSearchTimer.current) {
      clearTimeout(clientSearchTimer.current);
    }
    if (trimmed.length >= 2) {
      clientSearchTimer.current = setTimeout(() => {
        fetchClientSuggestions(trimmed);
      }, 250);
    } else {
      setClientSuggestions([]);
      setShowClientSuggestions(false);
    }
    const match = clientSuggestions.find(
      (suggestion) => suggestion.name?.toLowerCase() === normalized
    );
    if (match) {
      setClientEmail(match.email || "");
      setClientPhone(match.phone || "");
      setShowClientSuggestions(false);
      setSelectedClientId(match.id);
    } else if (!trimmed) {
      setClientPhone("");
      setShowClientSuggestions(false);
      setSelectedClientId(null);
    } else {
      setShowClientSuggestions(Boolean(clientSuggestions.length));
      setSelectedClientId(null);
    }
  };

  const handleOpenAddContactModal = () => {
    setNewContactName(clientName);
    setNewContactPhone("");
    setNewContactEmail("");
    setClientAddModalOpen(true);
  };

  const handleSaveContact = async () => {
    if (!newContactName.trim() || !newContactPhone.trim()) {
      setNotification({
        type: "danger",
        message: "Nome e telefone são obrigatórios para salvar um contato.",
      });
      return;
    }
    setIsSavingContact(true);
    try {
      const customFieldPayload = leadCustomFieldSchema
        .map((field) => {
          const rawValue = guessCustomFieldValue(field);
          const normalized = formatCustomFieldValueForPayload(field, rawValue);
          const isCheckbox = ["checkbox", "boolean"].includes(
            String(field.type || "").toLowerCase()
          );
          const shouldSend = isCheckbox
            ? normalized === "true"
            : String(normalized).trim().length > 0;
          if (!shouldSend) {
            return null;
          }
          return {
            customFieldId: field.id,
            value: normalized,
          };
        })
        .filter(Boolean);

      const payload = {
        name: newContactName.trim(),
        phoneNumber: newContactPhone.trim(),
        email: newContactEmail?.trim() || null,
        source: "manual",
        ownerId: selectedSellerId ? Number(selectedSellerId) : null,
        customFieldValues: customFieldPayload,
        leadType: "lead",
      };

      const response = await axiosInstance.post(LEADS.CREATE, payload);
      const saved =
        response?.data?.data ||
        response?.data ||
        response?.data?.lead ||
        null;
      if (!saved) {
        throw new Error("Não foi possível salvar o contato.");
      }
      setClientSuggestions((prev) => [
        saved,
        ...prev.filter((entry) => entry?.name !== saved.name),
      ]);
      setClientName(saved.name || "");
      setClientPhone(saved.phoneNumber || saved.phone || "");
      setClientEmail(saved.email || "");
      setSelectedClientId(saved.id || saved.leadId || null);
      setClientAddModalOpen(false);
      setNewContactName("");
      setNewContactPhone("");
      setNewContactEmail("");
      setNotification({
        type: "success",
        message: "Contato adicionado ao lead.",
      });
    } catch (error) {
      setNotification({
        type: "danger",
        message:
          error?.response?.data?.message ||
          "Não foi possível adicionar o contato ao lead.",
      });
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setClientName(suggestion.name);
    setClientEmail(suggestion.email || "");
    setClientPhone(suggestion.phone || "");
    setShowClientSuggestions(false);
    setSelectedClientId(suggestion.id);
  };

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + (Number(item.unitPrice) || 0) * (item.quantity || 0),
        0
      ),
    [items]
  );

  const totalDiscount = useMemo(
    () =>
      items.reduce((sum, item) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = item.quantity || 0;
        const discount = Math.max(0, Math.min(100, item.discount || 0));
        const subtotalItem = unitPrice * quantity;
        const discountValue = (subtotalItem * discount) / 100;
        return sum + discountValue;
      }, 0),
    [items]
  );

  const total = Math.max(0, subtotal - totalDiscount);
  const selectedTemplate =
    layoutTemplates.find((op) => op.id === selectedTemplateId) ||
    layoutTemplates[0] ||
    DEFAULT_TEMPLATE_OPTIONS[0];

  const validityDisplay = validityDate
    ? new Date(validityDate).toLocaleDateString("pt-BR")
    : "";

  const clientBlock = (
    <section className="grid gap-4 text-sm text-slate-600 sm:grid-cols-2">
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
          Cliente
        </p>
        <p className="text-base font-semibold text-slate-900">
          {clientName || "Cliente"}
        </p>
      </div>
      {selectedSeller && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
            Vendedor
          </p>
          <p className="text-base font-semibold text-slate-900">
            {selectedSeller.name || selectedSeller.email || "Vendedor"}
          </p>
        </div>
      )}
      {clientNote && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
            Observações
          </p>
          <p className="text-sm text-slate-500">{clientNote}</p>
        </div>
      )}
      {(clientEmail || clientPhone) && (
        <div className="space-y-1">
          {clientEmail && (
            <>
              <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
                E-mail
              </p>
              <p className="text-sm text-slate-500">{clientEmail}</p>
            </>
          )}
          {clientPhone && (
            <>
              <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Telefone
              </p>
              <p className="text-sm text-slate-500">{clientPhone}</p>
            </>
          )}
        </div>
      )}
    </section>
  );

  const totalsCard = (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
      <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
        Resumo financeiro
      </p>
      <div className="mt-3 space-y-2 text-sm text-slate-600">
        <div className="flex justify-between">
          <span>Subtotal</span>
          <strong className="text-slate-900">{formatCurrency(subtotal)}</strong>
        </div>
        <div className="flex justify-between">
          <span>Descontos aplicados</span>
          <strong className="text-slate-900">
            -{formatCurrency(totalDiscount)}
          </strong>
        </div>
        <div className="flex justify-between border-t border-slate-100 pt-3 text-base font-semibold text-slate-900">
          <span>Total</span>
          <span>{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );

  const itemsTable = (
    <div>
      <table className="w-full text-sm text-slate-900">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.4em] text-slate-400">
            <th className="pb-3">Descrição</th>
            <th className="pb-3">Qtd</th>
            <th className="pb-3">Valor Unit.</th>
            <th className="pb-3">Total</th>
          </tr>
        </thead>
        <tbody className="text-xs text-slate-800">
          {items.map((item) => {
            const unitPrice = Number(item.unitPrice) || 0;
            const itemTotal = unitPrice * (item.quantity || 0);
            const itemDiscount = (itemTotal * (item.discount || 0)) / 100;
            return (
              <tr key={item.id} className="h-14 border-b border-slate-100">
                <td>
                  <p className="font-semibold text-slate-900">{item.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {item.description}
                  </p>
                </td>
                <td>{item.quantity}</td>
                <td>{formatCurrency(unitPrice)}</td>
                <td>{formatCurrency(itemTotal - itemDiscount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderPreviewLayout = () => {
    switch (selectedTemplate.layout) {
      case "summaryFirst":
        return (
          <>
            {totalsCard}
            {clientBlock}
            <div className="overflow-x-auto">{itemsTable}</div>
          </>
        );
      case "sideBySide":
        return (
          <>
            <div className="overflow-x-auto">{itemsTable}</div>
            <div className="grid gap-4 lg:grid-cols-2">
              <div>{clientBlock}</div>
              <div>{totalsCard}</div>
            </div>
          </>
        );
      default:
        return (
          <>
            {clientBlock}
            <div className="overflow-x-auto">{itemsTable}</div>
            {totalsCard}
          </>
        );
    }
  };

  const previewPanel = (
    <div
      ref={pdfRef}
      className="mx-auto max-w-4xl space-y-6 rounded-2xl border border-slate-200 bg-white p-8 text-slate-900 shadow-lg"
    >
      <header className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-orange-500">
          Relinq Sales
        </p>
        <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-2xl font-bold">Proposta Comercial</h3>
          <div className="text-xs text-slate-500 flex gap-3">
            {referenceNumber}
            <span className="ml-4 block md:inline">Emissão: {issueDate}</span>
            <span className="ml-4 block md:inline">
              Válido até: {validityDisplay}
            </span>
          </div>
        </div>
      </header>
      {renderPreviewLayout()}
      <footer className="border-t border-slate-100 pt-4 text-[11px] uppercase tracking-[0.3em] text-slate-400">
        Proposta gerada automaticamente. Sujeita à confirmação de condições
        comerciais.
      </footer>
    </div>
  );

  const hasFilteredItems = filteredCatalog.length > 0;
  const selectedProduct =
    filteredCatalog.find((product) => product.id === selectedProductId) ||
    filteredCatalog[0] ||
    null;

  const handleGeneratePdf = async ({ showNotification = true } = {}) => {
    if (!items.length) {
      const message = "Adicione itens antes de gerar o PDF.";
      if (showNotification) {
        setNotification({ type: "danger", message });
      }
      throw new Error(message);
    }
    if (!pdfRef.current) {
      const message = "Pré-visualização indisponível para gerar o PDF.";
      if (showNotification) {
        setNotification({ type: "danger", message });
      }
      throw new Error(message);
    }

    try {
      if (typeof document !== "undefined" && document.fonts?.ready) {
        await document.fonts.ready;
      }
    } catch (fontError) {
      console.warn("fonts not ready", fontError);
    }

    await new Promise((resolve) => setTimeout(resolve, 200));

    const rect = pdfRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      const message =
        "Pré-visualização não renderizada ainda; tente novamente em instantes.";
      if (showNotification) {
        setNotification({ type: "danger", message });
      }
      throw new Error(message);
    }

    const filename = `${referenceNumber}.pdf`;
    const pdfPromise = html2pdf()
      .set({
        margin: 10,
        filename,
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        image: { type: "jpeg", quality: 0.98 },
      })
      .from(pdfRef.current)
      .save();

    if (showNotification) {
      pdfPromise.then(() =>
        setNotification({
          type: "success",
          message: "PDF preparado com sucesso.",
        })
      );
    }

    return pdfPromise;
  };

  const handleSaveBudget = async () => {
    if (!items.length) {
      setNotification({
        type: "danger",
        message: "Adicione itens antes de salvar o orçamento.",
      });
      return;
    }
    if (!selectedClientId) {
      setNotification({
        type: "danger",
        message: "Associe um contato antes de salvar o orçamento.",
      });
      return;
    }

    setIsSaving(true);
    setShowPreviewModal(false);
    try {
      const payloadItems = items.map((item) => ({
        productId: item.productId || null,
        name: item.name,
        description: item.description || null,
        type: item.type || null,
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        discountPercentage: Math.min(
          100,
          Math.max(Number(item.discount) || 0, 0)
        ),
        unit: item.unit || null,
      }));

      const payload = {
        reference: referenceNumber,
        leadId: selectedClientId,
        clientName: clientName || null,
        clientEmail: clientEmail || null,
        clientNote: clientNote || null,
        templateId: selectedTemplate?.id || null,
        ownerId: selectedSellerId ? Number(selectedSellerId) : null,
        issuedAt: new Date().toISOString(),
        validUntil: validityDate ? new Date(validityDate).toISOString() : null,
        items: payloadItems,
      };

      await axiosInstance.post(BUDGETS.CREATE, payload);
      setShowPreviewModal(true);
      await new Promise((resolve) => setTimeout(resolve, 400));
      await handleGeneratePdf({ showNotification: false });
      await new Promise((resolve) => setTimeout(resolve, 600));
      setNotification({
        type: "success",
        message: "Orçamento salvo com sucesso.",
      });
      navigate("/budgets", {
        state: {
          toast: {
            id: Date.now(),
            message: "Orçamento salvo com sucesso.",
          },
        },
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível salvar o orçamento.";
      setNotification({ type: "danger", message });
    } finally {
      setIsSaving(false);
      setShowPreviewModal(false);
    }
  };

  const breadcrumbItems = [
    { label: "Orçamentos", href: "/budgets" },
    { label: "Gerar orçamento", current: true },
  ];

  return (
    <div className="space-y-3">
      <CorporateBreadcrumb items={breadcrumbItems} />
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
            Orçamentos
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">
            Gere orçamentos para seus clientes
          </h1>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-6">
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm  space-y-4">
            <p className="text-sm font-medium text-slate-500">
              Informações do contato
            </p>
            <div className="mt-3 space-y-3 text-sm text-slate-600">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Contato
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 focus:border-slate-400"
                      value={clientName}
                      onChange={(e) => handleClientNameChange(e.target.value)}
                      placeholder="Nome do contato"
                      onFocus={() =>
                        setShowClientSuggestions(
                          Boolean(clientSuggestions.length)
                        )
                      }
                      onBlur={() =>
                        setTimeout(() => setShowClientSuggestions(false), 120)
                      }
                    />
                    {showClientSuggestions && (
                      <ul className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-auto rounded-2xl border border-gray-200 bg-white shadow-lg z-10">
                        {clientSuggestions.length ? (
                          clientSuggestions.map((suggestion) => (
                            <li
                              key={suggestion.id}
                              className="cursor-pointer px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                              onMouseDown={() =>
                                handleSuggestionSelect(suggestion)
                              }
                            >
                              <div className="font-semibold text-slate-900">
                                {suggestion.name}
                              </div>
                              <p className="text-xs text-slate-500">
                                {suggestion.email || suggestion.phone}
                              </p>
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-3 text-sm text-slate-500">
                            Nenhum cliente encontrado.
                            <button
                              type="button"
                              className="ml-2 text-xs font-semibold text-orange-600 underline"
                              onMouseDown={handleOpenAddContactModal}
                            >
                              Adicionar contato
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                    Vendedor
                  </label>
                  <select
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 focus:border-slate-400"
                    value={selectedSellerId}
                    onChange={(e) => setSelectedSellerId(e.target.value)}
                    disabled={isLoadingSellers}
                  >
                    <option value="">
                      {isLoadingSellers
                        ? "Carregando vendedores..."
                        : "Selecione um vendedor"}
                    </option>
                    {sellerOptions.map((seller) => (
                      <option key={seller.id} value={String(seller.id)}>
                        {seller.name || seller.email || `Vendedor ${seller.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                {(clientEmail || clientPhone) && (
                  <div className="mt-2 space-y-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    {clientEmail && (
                      <>
                        <p className="uppercase tracking-[0.3em] text-[10px] text-slate-400">
                          E-mail
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {clientEmail}
                        </p>
                      </>
                    )}
                    {clientPhone && (
                      <>
                        <p className="uppercase tracking-[0.3em] text-[10px] text-slate-400">
                          Telefone
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {clientPhone}
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Observações do orçamento
                </label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 focus:border-slate-400"
                  rows={3}
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm  space-y-3">
            <p className="text-sm font-medium text-slate-500">
              Informações do orçamento
            </p>
            <div className="space-y-2 text-sm text-slate-600">
              <label className="text-2xs uppercase tracking-[0.3em] text-slate-400">
                Validade do orçamento
              </label>
              <input
                type="date"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                value={validityDate}
                onChange={(e) => setValidityDate(e.target.value)}
              />

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Template do PDF
                </p>
                <div className="flex flex-wrap gap-2">
                  {layoutTemplates.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`rounded-2xl border px-3 py-1.5 text-xs font-semibold transition ${
                        selectedTemplateId === option.id
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-800"
                      }`}
                      onClick={() => setSelectedTemplateId(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500">
                  {selectedTemplate.description}
                </p>
                <div className="space-y-2 pt-3">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Templates salvos
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400"
                      value={selectedPresetId}
                      onChange={(e) => setSelectedPresetId(e.target.value)}
                      disabled={!savedTemplates.length}
                    >
                      <option value="">
                        {savedTemplates.length
                          ? "Selecione um template salvo"
                          : "Nenhum template salvo"}
                      </option>
                      {savedTemplates.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="xs"
                      color="success"
                      onClick={handleApplyPreset}
                      disabled={!selectedPresetId}
                    >
                      Aplicar
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      onClick={() => setPresetModalOpen(true)}
                      disabled={!items.length}
                    >
                      Salvar template
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:col-span-4">
          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
                Itens do orçamento
              </p>
              <h2 className="text-lg font-semibold text-slate-900">
                Conferir e ajustar
              </h2>
            </div>
            <Button size="sm" onClick={() => setIsCatalogModalOpen(true)}>
              Adicionar do catálogo
            </Button>
          </div>
          <div
            className="overflow-x-auto overflow-y-scroll"
            style={{ maxHeight: "600px" }}
          >
            <table className="w-full table-auto text-sm text-slate-800">
              <thead className="text-xs uppercase tracking-[0.3em] text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Nome</th>
                  <th className="px-3 py-2 text-left">Qtd</th>
                  <th className="px-3 py-2 text-left">Desconto %</th>
                  <th className="px-3 py-2 text-left">Subtotal</th>
                  <th className="px-3 py-2 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const unitPrice = Number(item.unitPrice) || 0;
                  const itemTotal = unitPrice * (item.quantity || 0);
                  const itemDiscount = (itemTotal * (item.discount || 0)) / 100;
                  return (
                    <tr key={item.id} className="border-t border-slate-100">
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-900">
                          {item.name}
                        </div>
                      </td>
                      <td className="px-3 py-3 max-w-[120px]">
                        <input
                          className="w-20 rounded-xl border border-slate-200 px-2 py-1 text-sm"
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(item.id, "quantity", e.target.value)
                          }
                        />
                      </td>
                      <td className="px-3 py-3 max-w-[140px]">
                        <input
                          className="w-20 rounded-xl border border-slate-200 px-2 py-1 text-sm"
                          type="number"
                          min={0}
                          max={100}
                          step="0.01"
                          value={item.discount}
                          onChange={(e) =>
                            updateItem(item.id, "discount", e.target.value)
                          }
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div>{formatCurrency(itemTotal - itemDiscount)}</div>
                        <p className="text-[11px] text-slate-400">
                          {formatCurrency(itemTotal)}{" "}
                          {itemDiscount > 0 && (
                            <span className="text-red-500">
                              - {formatCurrency(itemDiscount)}
                            </span>
                          )}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          className="text-sm font-semibold text-orange-600 hover:text-orange-800"
                          onClick={() => removeItem(item.id)}
                        >
                          <FiTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!items.length && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-8 text-center text-sm text-slate-500"
                    >
                      Nenhum item adicionado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
            Pré-visualização
          </p>
          <p className="text-sm text-slate-500">
            Clique em “Ver pré-visualização” para ver o PDF antes de exportar.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => setShowPreviewModal(true)}
            disabled={!items.length}
          >
            Ver pré-visualização
          </Button>
          <Button
            size="sm"
            color="success"
            onClick={handleSaveBudget}
            disabled={!items.length || isSaving}
          >
            {isSaving ? "Preparando..." : "Enviar orçamento"}
          </Button>
        </div>
      </div>

      <Modal
        show={showPreviewModal}
        size="4xl"
        popup
        onClose={() => setShowPreviewModal(false)}
      >
        <Modal.Header />
        <Modal.Body className="p-4">{previewPanel}</Modal.Body>
      </Modal>
      <Modal
        show={clientAddModalOpen}
        size="md"
        popup
        onClose={() => setClientAddModalOpen(false)}
      >
        <Modal.Header>
          <div className="text-base px-2">Adicionar contato</div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Nome
              </label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Telefone
              </label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                E-mail (opcional)
              </label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={newContactEmail}
                onChange={(e) => setNewContactEmail(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                color="gray"
                size="sm"
                onClick={() => setClientAddModalOpen(false)}
                disabled={isSavingContact}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                color="success"
                onClick={handleSaveContact}
                disabled={isSavingContact}
              >
                {isSavingContact ? "Salvando..." : "Salvar contato"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={presetModalOpen}
        size="md"
        popup
        onClose={() => setPresetModalOpen(false)}
      >
        <Modal.Header>
          <div className="text-base px-2">Salvar template</div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Nome do template
              </label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Ex: Proposta padronizada"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Descrição
              </label>
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                rows={3}
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                placeholder="Opcional, descreva a finalidade deste template"
              />
            </div>
            <p className="text-xs text-slate-500">
              Este template salva os produtos, quantidades, preços e descontos
              atualmente selecionados.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                color="gray"
                size="sm"
                onClick={() => setPresetModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                color="success"
                onClick={handleSavePreset}
                disabled={!items.length || isSavingPreset}
              >
                {isSavingPreset ? "Salvando..." : "Salvar template"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <AnimatedToast
        open={Boolean(animatedToast)}
        message={animatedToast?.message}
        onClose={() => setAnimatedToast(null)}
      />

      <Modal
        show={isCatalogModalOpen}
        size="3xl"
        popup
        onClose={() => setIsCatalogModalOpen(false)}
      >
        <Modal.Header>
          <span className="text-sm">Catálogo de produtos e serviços</span>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-gray-50 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                    Categoria
                  </p>
                  <select
                    className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                  >
                    <option value="">Todas as categorias</option>
                    {isLoadingCategories ? (
                      <option value="" disabled>
                        Carregando categorias...
                      </option>
                    ) : categories.length ? (
                      categories.map((category) => (
                        <option key={category.id} value={`${category.id}`}>
                          {category.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        Nenhuma categoria encontrada
                      </option>
                    )}
                  </select>
                  {!isLoadingCategories && !categories.length && (
                    <p className="mt-2 text-xs text-slate-500">
                      Cadastre categorias para organizar o catálogo.
                    </p>
                  )}
                </div>
                <div className="rounded-2xl bg-gray-50 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                    Buscar
                  </p>
                  <input
                    type="search"
                    placeholder="Pesquise produtos ou serviços"
                    className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    A pesquisa considera nomes e descrições.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-100 bg-white shadow-inner">
                <div className="flex flex-col gap-1 px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                    <span>Itens disponíveis</span>
                    <span className="text-xs text-slate-500">
                      {filteredCatalog.length} encontrados
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>
                      Selecionados: {selectedCatalogIds.length || 0}
                    </span>
                    <Button
                      size="xs"
                      color="success"
                      disabled={!selectedCatalogIds.length}
                      onClick={handleAddSelectedCatalogItems}
                    >
                      Adicionar selecionados
                    </Button>
                  </div>
                </div>
                  <div className="max-h-[340px] divide-y divide-slate-100 overflow-y-auto">
                    {filteredCatalog.length ? (
                      filteredCatalog.map((product) => (
                      <div
                        key={product.id}
                        role="button"
                        tabIndex={0}
                        className={`flex w-full cursor-pointer flex-col gap-1 px-4 py-2 transition ${
                          selectedProductId === product.id
                            ? "bg-slate-50"
                            : "hover:bg-slate-100"
                        }`}
                        onClick={() => handleCatalogItemClick(product)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleCatalogItemClick(product);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-4 w-4 items-center justify-center rounded border ${
                              selectedCatalogIds.includes(String(product.id))
                                ? "border-orange-500 bg-orange-500 text-white"
                                : "border-slate-300 bg-white text-transparent"
                            }`}
                          >
                            {selectedCatalogIds.includes(String(product.id)) && (
                              <span className="text-[10px] font-bold">✓</span>
                            )}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-slate-900">
                                {product.name}
                              </span>
                              <span className="text-sm font-semibold text-slate-900 mt-1">
                                {formatCurrency(product.unitPrice || 0)}
                              </span>
                            </div>
                            <p className="text-2xs uppercase tracking-[0.3em] text-slate-400">
                              {product.type}
                            </p>
                          </div>
                        </div>
                      </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center text-xs text-slate-500">
                        {selectedCategoryId
                          ? "Nenhum produto disponível para a categoria selecionada."
                          : "Nenhum item disponível no catálogo."}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BudgetGeneratorPage;
